import React, { useMemo } from "react";

import {
  ActionDropdown,
  HeaderImage,
  IActionDropdownItem,
  Placeholder,
} from "shared/components";
import { Image, Menu } from "shared/assets/icons";
import { copy } from "shared/assets/copy";

import { IVenue } from "Venues/types";
import {
  StageInfo,
  AddressInfo,
  Logo,
  LastUpdatedInfo,
  ContactInfo,
} from "Venues/components";
import {
  VenueHeaderContainer,
  VenueHeaderContent,
  VenueHeaderActions,
  ImageContainer,
  DummyImageContainer,
  LastUpdatesContainer,
} from "./styles";
import { VenueTitle } from "../VenueInfo/VenueTitle";
import { getImageURLWithStyle } from "shared/utils";
import CoverImage from "shared/assets/images/cover-placeholder.jpg";
import { IImageStylePreset } from "shared/types";
import { useVenueMap, useVenueRoute } from "Venues/utils";
import { VENUE_PATHS } from "Venues/router";

const { placeholder, menu } = copy;

interface BaseHeaderProps extends Partial<IVenue> {
  stageTitle?: string;
  actions?: IActionDropdownItem[];
  showPlaceholder?: boolean;
  theme?: "basic" | "full";
  isCenter?: boolean;
  editBlockUrl?: string | null;
}

export const BaseHeader: React.FC<BaseHeaderProps> = ({
  showPlaceholder,
  theme = "basic",
  actions,
  id,
  title,
  alternativeName,
  stageTitle,
  address,
  contact,
  stages,
  updatedAt,
  logo,
  coverImage,
  meta,
  isCenter,
  editBlockUrl,
}) => {
  const showDetails = theme === "full";

  const actionSelectProps = useMemo(
    () => ({
      label: menu.open,
      options: actions || [],
      triggerIcon: Menu,
      wrapOptions: true,
      dataTestId: "venue_header_actions",
    }),
    [actions]
  );

  const { renderMapModal, renderMapPreview } = useVenueMap({ title, address });

  const { getPath } = useVenueRoute();
  const venueUrl = useMemo(() => getPath(VENUE_PATHS.VENUE), [getPath]);

  return (
    <>
      {(coverImage || showPlaceholder) && (
        <HeaderImage isCenter={isCenter}>
          {coverImage ? (
            <img
              src={getImageURLWithStyle({
                ...coverImage,
                style: IImageStylePreset.cover,
              })}
              alt={coverImage.description || `${copy.a11y.coverImage} ${title}`}
            />
          ) : (
            <Placeholder
              label={placeholder.coverImage}
              icon={Image}
              backgroundImage={CoverImage}
              noBorder
            />
          )}
        </HeaderImage>
      )}
      <VenueHeaderContainer hasActions={actions !== undefined}>
        <VenueHeaderContent>
          <VenueTitle
            title={title}
            alternativeName={alternativeName}
            stageTitle={stageTitle}
            meta={meta}
            showPlaceholder={showPlaceholder}
            size="huge"
            venueUrl={id ? venueUrl : undefined}
          />
          <AddressInfo address={address} showPlaceholder={showPlaceholder} />
          {showDetails && (
            <ContactInfo {...contact} showPlaceholder={showPlaceholder} />
          )}
          {showDetails && id && (
            <StageInfo
              stages={stages}
              venueId={id}
              editBlockUrl={editBlockUrl}
            />
          )}
        </VenueHeaderContent>
        <VenueHeaderActions>
          {actions && (
            <ActionDropdown placement="bottom-end" {...actionSelectProps} />
          )}
        </VenueHeaderActions>
        <ImageContainer>
          {logo ? (
            <Logo
              logo={logo}
              alt={logo.description || `${copy.a11y.logo} ${title}`}
            />
          ) : (
            showPlaceholder && (
              <DummyImageContainer>
                <Placeholder label={placeholder.logo} icon={Image} isRounded />
              </DummyImageContainer>
            )
          )}
          {showDetails && (
            <>
              {renderMapPreview()}
              {renderMapModal()}
            </>
          )}
        </ImageContainer>
        {showDetails && updatedAt && (
          <LastUpdatesContainer>
            <LastUpdatedInfo time={updatedAt} />
          </LastUpdatesContainer>
        )}
      </VenueHeaderContainer>
    </>
  );
};
